import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Cookie, Settings, BarChart, Shield, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookiePolicyPage = () => {
  const lastUpdated = "January 15, 2025";

  return (
    <div className="cookie-policy-page">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="page-header">
            <Link to="/" className="back-link">
              <ArrowLeft size={20} />
              Back to Home
            </Link>
            <h1>Cookie Policy</h1>
            <p className="last-updated">Last updated: {lastUpdated}</p>
          </div>

          <div className="cookie-content">
            <section className="cookie-section">
              <h2>What Are Cookies?</h2>
              <p>
                Cookies are small text files that are stored on your device when you visit our website or use our mobile application. They help us provide you with a better experience by remembering your preferences and analyzing how you use our services.
              </p>
            </section>

            <section className="cookie-section">
              <h2>How We Use Cookies</h2>
              <p>
                Snapcipe AI uses cookies and similar technologies to enhance your experience, analyze usage patterns, and improve our services. We are committed to being transparent about our cookie usage.
              </p>
            </section>

            <section className="cookie-section">
              <h2>Types of Cookies We Use</h2>
              
              <div className="cookie-types">
                <div className="cookie-type">
                  <div className="cookie-icon">
                    <Shield size={24} />
                  </div>
                  <div className="cookie-details">
                    <h3>Essential Cookies</h3>
                    <p><strong>Purpose:</strong> Required for basic website functionality</p>
                    <p><strong>Duration:</strong> Session or up to 1 year</p>
                    <p><strong>Examples:</strong></p>
                    <ul>
                      <li>User authentication and session management</li>
                      <li>Security and fraud prevention</li>
                      <li>Basic website functionality</li>
                    </ul>
                    <p className="cookie-control"><strong>Can be disabled:</strong> No (required for service)</p>
                  </div>
                </div>

                <div className="cookie-type">
                  <div className="cookie-icon">
                    <BarChart size={24} />
                  </div>
                  <div className="cookie-details">
                    <h3>Analytics Cookies</h3>
                    <p><strong>Purpose:</strong> Help us understand how users interact with our app</p>
                    <p><strong>Duration:</strong> Up to 2 years</p>
                    <p><strong>Examples:</strong></p>
                    <ul>
                      <li>Page views and user navigation patterns</li>
                      <li>Feature usage statistics</li>
                      <li>Performance monitoring</li>
                      <li>Error tracking and debugging</li>
                    </ul>
                    <p className="cookie-control"><strong>Can be disabled:</strong> Yes</p>
                  </div>
                </div>

                <div className="cookie-type">
                  <div className="cookie-icon">
                    <Settings size={24} />
                  </div>
                  <div className="cookie-details">
                    <h3>Functional Cookies</h3>
                    <p><strong>Purpose:</strong> Remember your preferences and enhance functionality</p>
                    <p><strong>Duration:</strong> Up to 1 year</p>
                    <p><strong>Examples:</strong></p>
                    <ul>
                      <li>Language and region preferences</li>
                      <li>Theme and display settings</li>
                      <li>Saved recipes and favorites</li>
                      <li>Dietary restrictions and preferences</li>
                    </ul>
                    <p className="cookie-control"><strong>Can be disabled:</strong> Yes (may affect functionality)</p>
                  </div>
                </div>

                <div className="cookie-type">
                  <div className="cookie-icon">
                    <Cookie size={24} />
                  </div>
                  <div className="cookie-details">
                    <h3>Marketing Cookies</h3>
                    <p><strong>Purpose:</strong> Deliver relevant advertisements and measure campaign effectiveness</p>
                    <p><strong>Duration:</strong> Up to 1 year</p>
                    <p><strong>Examples:</strong></p>
                    <ul>
                      <li>Personalized content recommendations</li>
                      <li>Social media integration</li>
                      <li>Advertising campaign tracking</li>
                    </ul>
                    <p className="cookie-control"><strong>Can be disabled:</strong> Yes</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="cookie-section">
              <h2>Third-Party Cookies</h2>
              <p>We may use third-party services that set their own cookies:</p>
              
              <div className="third-party-services">
                <div className="service">
                  <h4>Google Analytics</h4>
                  <p>Helps us analyze website traffic and user behavior</p>
                  <p><strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="external-link">Google Privacy Policy</a></p>
                </div>
                
                <div className="service">
                  <h4>Authentication Services</h4>
                  <p>Enable secure login functionality</p>
                  <p><strong>Includes:</strong> Apple Sign In and email authentication</p>
                </div>
                
                <div className="service">
                  <h4>Customer Support</h4>
                  <p>Powers our help desk and customer service features</p>
                  <p><strong>Purpose:</strong> Provide efficient customer support</p>
                </div>
              </div>
            </section>

            <section className="cookie-section">
              <h2>Mobile App Data Collection</h2>
              <p>
                In addition to cookies on our website, our mobile app collects similar information through:
              </p>
              <ul>
                <li><strong>Device Identifiers:</strong> Unique identifiers for analytics and personalization</li>
                <li><strong>App Usage Data:</strong> Feature usage, session duration, and user interactions</li>
                <li><strong>Crash Reports:</strong> Technical data to improve app stability</li>
                <li><strong>Push Notification Tokens:</strong> To send relevant notifications</li>
              </ul>
            </section>

            <section className="cookie-section">
              <h2>Managing Your Cookie Preferences</h2>
              
              <div className="cookie-management">
                <div className="management-method">
                  <h3>Browser Settings</h3>
                  <p>Most web browsers allow you to control cookies through their settings:</p>
                  <ul>
                    <li>Block all cookies</li>
                    <li>Block third-party cookies only</li>
                    <li>Delete existing cookies</li>
                    <li>Receive notifications when cookies are set</li>
                  </ul>
                </div>

                <div className="management-method">
                  <h3>Mobile App Settings</h3>
                  <p>In our mobile app, you can control data collection through:</p>
                  <ul>
                    <li>In-app privacy settings</li>
                    <li>Device privacy settings</li>
                    <li>Opt-out of analytics tracking</li>
                    <li>Disable personalized features</li>
                  </ul>
                </div>

                <div className="management-method">
                  <h3>Opt-Out Links</h3>
                  <p>You can opt out of specific tracking services:</p>
                  <ul>
                    <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="external-link">Google Analytics Opt-out</a></li>
                    <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="external-link">Safari Cookie Settings</a></li>
                    <li>Industry opt-out tools: <a href="http://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="external-link">Digital Advertising Alliance</a></li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="cookie-section">
              <h2>Impact of Disabling Cookies</h2>
              <div className="impact-warning">
                <AlertTriangle size={20} />
                <div>
                  <h4>Important Notice</h4>
                  <p>Disabling certain cookies may affect your experience:</p>
                  <ul>
                    <li>Some features may not work properly</li>
                    <li>You may need to re-enter preferences each visit</li>
                    <li>Personalized recommendations may be less relevant</li>
                    <li>We may not be able to remember your settings</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="cookie-section">
              <h2>Data Retention</h2>
              <p>We retain cookie data for different periods based on their purpose:</p>
              <ul>
                <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
                <li><strong>Functional cookies:</strong> Up to 1 year</li>
                <li><strong>Analytics cookies:</strong> Up to 2 years</li>
                <li><strong>Marketing cookies:</strong> Up to 1 year</li>
              </ul>
            </section>

            <section className="cookie-section">
              <h2>International Data Transfers</h2>
              <p>
                Some of our third-party services may transfer your data internationally. We ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
              </p>
            </section>

            <section className="cookie-section">
              <h2>Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated policy on our website and in our app.
              </p>
            </section>

            <section className="cookie-section">
              <h2>Contact Us</h2>
              <p>
                If you have questions about our use of cookies or this policy, please contact us:
              </p>
              <div className="contact-info">
                <p><strong>Email:</strong> support@snapcipe.app</p>
                <p><strong>Subject Line:</strong> Cookie Policy Inquiry</p>
                <p><strong>Address:</strong> Snapcipe AI, Zurich, Switzerland</p>
              </div>
            </section>

            <div className="policy-links">
              <p>Related Policies:</p>
              <div className="policy-link-grid">
                <Link to="/privacy-policy" className="policy-link">Privacy Policy</Link>
                <Link to="/terms-of-service" className="policy-link">Terms of Service</Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;